import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation'
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen'
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons'
import { Card, Flex, Text, TextInput, Tooltip } from '@mantine/core'
import { clsx } from 'clsx'
import { useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreBookingDocument } from '@flecto/types'
import { Badge, BadgeEditable, Icon } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'
import { formatDateString } from '@flecto/utils'

import { useBookingDocumentCategories } from '../../hooks/useBookingDocumentCategories'
import { useBookingDocumentUtils } from '../../hooks/useBookingDocumentUtils'
import { SelectDocumentCategories } from '../SelectDocumentCategories/SelectDocumentCategories'
import styles from './styles.module.css'
import { UploadDocumentCardActions } from './UploadDocumentCardActions'

type UploadDocumentCardProps = {
  readOnly?: boolean
  loading?: boolean
  error?: boolean
  errorDescription?: {
    id: string
    message: string
  }
  document: CoreBookingDocument
  isAdding?: boolean
  isFlectoLink?: boolean
  onView?: () => void
  onChange?: (field: string, value: string | boolean) => void
  onDelete?: () => void
  onCancel?: () => void
  onRetry?: () => void

}

export const UploadDocumentCard = ({
  readOnly = false,
  document,
  errorDescription,
  loading = false,
  error = false,
  isAdding = false,
  isFlectoLink = false,
  onCancel,
  onChange,
  onDelete,
  onView,
  onRetry,
}: UploadDocumentCardProps) => {
  const { t } = useTranslation('saas', { keyPrefix: 'booking' })
  const { getCategory } = useBookingDocumentCategories()
  const selectedCategory = getCategory(document.category ?? 'others')
  const { isDesktop, isMobile } = useMediaQuery()
  const updateDifferentFromCreate = document.updated_at !== document.created_at
  const dateToShow = document.generated
    ? document.created_at
    : updateDifferentFromCreate
      ? document.updated_at
      : document.created_at

  const formattedDate = formatDateString({
    date: dateToShow ?? '',
    variant: 'date-brief',
  })

  const [name, setName] = useState(document.filename_base ?? '')

  const knownFileRejectionErrors = ['file-invalid-type', 'file-too-large']
  const { formatBySize } = useBookingDocumentUtils()

  const classText = clsx({
    [styles.textCardMobile]: isMobile,
    [styles.textCard]: !isMobile,
  })

  const getFileExtension = (fileName: string | undefined) => {
    const lastDotIndex = fileName?.lastIndexOf('.') || -1
    if (lastDotIndex !== -1) {
      return fileName?.substring(lastDotIndex)
    }
    return ''
  }

  const renderBody = () => {
    if (error) {
      return (
        <Flex direction={'row'} align={'center'} justify={'center'} gap={'md'}>
          <Icon
            icon={faCircleExclamation}
            color='red'
            size='lg'
            withCircleBackground
          />
          <Flex direction={'column'}>
            <Flex gap={'sm'} align={'baseline'}>
              <Text fw={500} c='red' size={'md'}>
                {t('booking_documents.upload_failed')}
              </Text>
              <Text c='red' fw={400} size={'sm'}>
                {errorDescription?.message}
              </Text>
            </Flex>
            <Flex direction={'row'} gap={'xs'} align={'flex-end'}>
              <Text
                c='red'
                data-testid='meta-filename'
                fw={400}
                size={'sm'}
                truncate
                className={classText}
              >
                {document.filename_base}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )
    }
    return (
      <Flex
        direction='row'
        align='center'
        justify='center'
        gap='md'
        wrap={'wrap'}
      >
        <SelectDocumentCategories
          value={document.category}
          readOnly={readOnly || loading || document.generated}
          onChange={(cat) => onChange?.('category', cat)}
        />
        <Flex direction={'column'}>
          <Flex gap='sm' align='center'>
            <Text
              fw={500}
              style={{ maxWidth: '150px' }}
              data-testid='meta-filename'
              className={classText}
              truncate
            >
              {document.filename_base}
              {!document.generated && getFileExtension(document?.filename)}
            </Text>
            {!readOnly && !loading && (
              <BadgeEditable
                className={styles.badgeEditName}
                value={<Icon icon={faPen} color='gray.6' size='xs' />}
                popoverProps={{
                  zIndex: 11000,
                }}
              >
                <BadgeEditable.Dropdown>
                  <TextInput
                    autoFocus
                    label={t('booking_documents.name')}
                    value={name ?? ''}
                    onChange={(e) => setName(e.target.value)}
                  />
                </BadgeEditable.Dropdown>
                <BadgeEditable.Confirm
                  onClick={() => {
                    if (name === '') {
                      setName(document.filename_base ?? '')
                      return
                    }
                    onChange?.('filename_base', name ?? '')
                  }}
                />
                <BadgeEditable.Close />
              </BadgeEditable>
            )}
            {document?.signed && (
              <Tooltip
                label={t('booking_documents.tooltip_icon_signed')}
                zIndex={11000}
                withinPortal
              >
                <Text mb={isFlectoLink ? -5 : 0}>
                  <Icon icon={faBadgeCheck} color='gray.6' />
                </Text>
              </Tooltip>
            )}
            <Badge
              color={selectedCategory?.color}
              variant='light'
              data-testid='badge-category'
            >
              <Text fw={500} c={selectedCategory?.color} size={'12px'}>
                {selectedCategory?.label}
              </Text>
            </Badge>
          </Flex>
          <Flex direction={'row'} gap={'xs'} align={'baseline'}>
            <Text c='gray.7' size={'sm'}>
              {formatBySize(document?.byte_size ?? 0)}
            </Text>
            {!isAdding && (
              <>
                <Text c='gray.3' ta='center'>
                  •
                </Text>
                <Text
                  fw={400}
                  c='gray.6'
                  data-testid='uploaded-at'
                  size={'12px'}
                >
                  {t(
                    document.generated
                      ? 'booking_documents.generated'
                      : updateDifferentFromCreate
                        ? 'booking_documents.updated'
                        : 'booking_documents.created',
                    {
                      replace: {
                        date: formattedDate,
                      },
                    }
                  )}
                </Text>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    )
  }

  return (
    <Card withBorder>
      <Flex
        justify={'space-between'}
        align={!isDesktop ? 'flex-start' : 'center'}
        gap={'lg'}
        wrap={'wrap'}
      >
        {renderBody()}
        <UploadDocumentCardActions
          document={document}
          visible_to_renter={document.visible_to_renter ?? false}
          loading={loading}
          error={error}
          errorDescription={errorDescription}
          onView={onView}
          onCancel={onCancel}
          onChange={onChange}
          onDelete={onDelete}
          onRetry={onRetry}
          readonly={readOnly}
          knownFileRejectionErrors={knownFileRejectionErrors}
          isFlectoLink={isFlectoLink}
        />
      </Flex>
    </Card>
  )
}
